<template>
    <div id="generate-ppt-build-new" class="position-relative">
        <div id="choose">
            <div id="choose-report" class="card">
                <div id="choose-segment__description">
                    <h4>Step 1: Select Report Pages</h4>
                    <p>Select the report pages you want to include in your presentation.</p>
                </div>

                <div id="choose-report_search-row" class="d-flex flex-column flex-sm-row gap-2 align-items-stretch">
                    <div class="search-box flex-grow-1 position-relative">
                        <input type="text" class="form-control" placeholder="Search by Report Name, Group Name or Description ..." aria-label="Search by Report Name, Group Name or Description ..." v-model="search_key" @input="searchPage">
                        <i class="fa fa-search"></i>
                    </div>
                </div>

                <div id="choose-report-list" class="d-flex flex-column">
                    <div v-if="filtered_page_list.length === 0" class="card long-card disabled">
                        <div class="card-body long-card__body row">
                            <div class="long-card__content">
                                <h4 class="mb-1">No reports Found</h4>
                            </div>
                        </div>
                    </div>

                    <div v-else v-for="(page, page_index) in available_page_list" :key="page.id" class="card long-card" @click="addPageSelectionToPPT(page, page_index)">
                        <div class="card-body long-card__body d-flex align-items-center">
                            <div class="long-card__content d-flex flex-row justfy-content-start gap-2">
                                <div class="d-flex flex-column flex-grow-1 gap-1">
                                    <div class="d-block">
                                        <div class="d-flex flex-row align-items-center position-relative">
                                            <h4>{{ page.name }}</h4>
                                            <small>
                                                <span class="badge">{{ page.group_name }}</span>
                                            </small>
                                        </div>
                                    </div>
                                    <p>{{ page.description }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="table-pagination">
                        <div class="table-pagination--group d-none d-md-flex">
                            <div class="position-relative">
                                <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination" aria-label="Num Per Page">
                                    <option value="5000">ALL</option>
                                    <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                </select>
                                <div class="select-display-text">
                                    Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                </div>
                            </div>
                        </div>

                        <div class="table-pagination--group d-none d-lg-flex">
                            {{ filtered_page_list.length === 0 ? '0' : item_interval }} of {{ filtered_page_list.length }}
                        </div>

                        <div class="table-pagination--group">
                            <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage" :disabled="page_index === 0">
                                <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Previous">
                            </button>

                            <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click="moveToPage(page)">
                                    {{ page }}
                                </button>
                            </div>
                            <div class="d-flex page-selector" v-else>
                                <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click="moveToPage(page)">
                                    {{ page }}
                                </button>

                                <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                ...
                                </button>
                                <button v-if="page_index >= 2 && page_index < page_index_array.length - 1" class="btn btn-none btn-icon-only-md current-page">
                                    {{ page_index + 1 }}
                                </button>

                                <button v-if="page_index < page_index_array.length - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                                ...
                                </button>
                                <button class="btn btn-none btn-icon-only-md" :class="{'current-page': page_index === page_index_array.length - 1}" @click="moveToPage(page_index_array.length)" :key="page_index_array.length">
                                    {{ page_index_array.length }}
                                </button>
                            </div>

                            <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage" :disabled="page_index === page_count - 1">
                                <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next">
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div id="selected-report" class="card gap-0">
                <div id="selected-report-description" class="row d-flex flex-lg-row">
                    <div class="col-lg-10 col-12">
                        <h4>Step 2: Manage the Page Order</h4>
                        <p>Change the order of the various pages or remove selected reports.</p>
                    </div>
                    <div class="col-lg-2 col-12" v-if="selected_page_list.length > 0">
                        <button type="button" class="btn btn-primary float-end my-2 me-1" @click="toggleFilterPanel">
                            <img class="icon pe-0" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="Filter">
                            <span>({{ filter_count }})</span>
                        </button>
                    </div>
                </div>

                <div id="selected-report-list" class="flex-grow-1 position-relative" :style="{'max-height': 'calc(100% - 60px)', 'overflow-y': 'auto'}">
                    <div class="position-absolute top-0 start-0 end-0 bottom-0">
                        <div class="position-relative">
                            <div v-if="selected_page_list.length === 0" class="card long-card disabled mt-3">
                                <div class="card-body long-card__body row">
                                    <div class="long-card__content">
                                        <h4 class="mb-1">No Pages Selected</h4>
                                        <p>Select a page from the list on the left to start building your presentation.</p>
                                    </div>
                                </div>
                            </div>
                            <div class="scroll-view d-flex flex-column flex-shrink-1 pe-3 pt-3" style="max-height: 100%;" v-else>
                                <draggable v-model="selected_page_list" handle=".handle" item-key="id">
                                    <template #item="{ element: page, index: page_index }">
                                        <div class="card long-card me-1 mb-3" style="cursor: default;">
                                            <div class="card-body long-card__body row">
                                                <div class="long-card__content">
                                                    <div class="row d-flex align-items-center position-relative">
                                                        <div class="col-1 float-start">
                                                            <div class="handle">
                                                                <img src="/img/icons/avid-icons/vertical-dots.svg" alt="drag" class="icon">
                                                            </div>
                                                        </div>
                                                        <div class="col-11">
                                                            <h4 class="mb-1">{{ page.name }}</h4>
                                                            <p class="mb-1">
                                                                <small>
                                                                    <span class="badge">{{ page.group_name }}</span>
                                                                </small>
                                                                <small v-if="page.sections.length">
                                                                    <span class="badge">{{ page.sections.length }} {{ page.sections.length > 1 ? 'Sections' : 'Section' }}</span>
                                                                </small>
                                                            </p>
                                                            <div class="d-flex">
                                                                <div class="w-100">
                                                                    <label>
                                                                        <input class="form-check-input me-1" type="checkbox" v-model="page.blend_sections">
                                                                        Blend sections
                                                                        <i class="fas fa-question-circle info-icon mt-1" data-bs-toggle="tooltip" title="In pages where the donors and revenue are split, it can be helpful to blend the sections together. This will alternate the charts in the first two sections."></i>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <button type="button" class="long-card-delete-btn btn" aria-label="Close" @click="removePageSelection(page, page_index)">
                                                <i class="fa fa-times" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Sticky footer moved inside Step 2 card -->
                <div class="sticky-footer">
                    <nav class="navbar navbar-expand d-flex justify-content-between row">
                        <div class="col-12">
                            <button class="btn btn-primary btn-lg float-end " @click.prevent="downloadPPT" :disabled="form.busy || selected_page_list.length === 0">
                                <span v-if="!form.busy">Download PPT</span>
                                <span v-else><span class="spinner-border spinner-border-sm me-2" role="status"><span class="visually-hidden"></span></span> Processing</span>
                            </button>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
        <transition name="slide-fade" duration="500">
            <div class="filter-panel" :class="{'open': is_filter_panel_expanded, 'visually-hidden': !is_filter_panel_expanded}">
                <div id="filter-panel--heading" class="p-4 pb-2">
                    <h3>Filters</h3>
                    <button type="button" class="btn btn-white float-end close-modal" @click="toggleFilterPanel" aria-label="Close">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                </div>
                <p class="mx-4 mb-2 small">Once a filter below is selected, it will be automatically applied to the presentation. If the the filter is not present as an option on the selected report(s) it will not be applied.</p>
                <generate-presentation-filter-panel :pages_selected="selected_page_list" :client="client" @updateFilters="updateFilters" @updateFilterCount="updateFilterCount"></generate-presentation-filter-panel>
            </div>
        </transition>

        <link rel="preload" href="/img/icons/avid-icons/vertical-dots.svg" as="image" type="image/svg+xml" />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import draggable from 'vuedraggable';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'GeneratePresentationBuildNew',
    components: { 
        draggable
    },
    setup() {
        const { showProgressNotification } = useNotifications();
        return {
            showProgressNotification
        };
    },
    props: {
        client: {
            type: Object,
            required: true
        },
        report_pages: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            search_key: '',
            filtered_page_list: [],
            available_page_list: [],
            selected_page_list: [],
            // pagination
            num_per_page: 5, // default to 5
            num_per_page_options: [5, 10, 15, 20, 25, 30, 50, 100],
            page_count: null,
            page_index: 0,
            page_index_array: [],
            item_interval: '',

            is_filter_panel_expanded: false,
            selected_filter_list: [],
            filter_count: 0,
            form: {
                successful: false,
                busy: false,
                error: false
            },
            export_id: null,
            pendingPresoName: null
        };
    },
    beforeMount() {
        this.resetFilteredPages();
    },
    mounted() {
        // Remove tooltip initialization from here since it's now handled in addPageSelectionToPPT
    },
    methods: {
        resetFilteredPages() {
            this.search_key = '';
            this.filtered_page_list = [...this.report_pages];
            this.filtered_page_list.sort((a, b) => (a.name > b.name) ? 1 : -1);
            this.reloadPagination();
        },
        clonePresentation(old_preso) {
            this.selected_page_list = [];
            this.resetFilteredPages();

            old_preso.sections.forEach(section => {
                for(let i = this.filtered_page_list.length - 1; i >= 0; i--) {
                    if(this.filtered_page_list[i].name === section.page.name) {
                        this.addPageSelectionToPPT(this.filtered_page_list[i], i);
                    }
                }
            });
        },
        searchPage() {
            let filtered_list = [];

            if (this.search_key === '') {
                filtered_list = this.report_pages;
                this.selected_page_list.forEach(page_tim => {
                    const page_id = page_tim.id;
                    const page_index = filtered_list.findIndex(page => page.id === page_id);
                    if (page_index !== -1) {
                        filtered_list.splice(page_index, 1);
                    }
                });
            } else {
                this.moveToPage(1);
                this.report_pages.forEach(page => {
                    if(page.name.toLowerCase().includes(this.search_key.toLowerCase()) ||
                       page.group_name.toLowerCase().includes(this.search_key.toLowerCase()) ||
                       page.description.toLowerCase().includes(this.search_key.toLowerCase())) {
                        filtered_list.push(page);
                    }
                });
            }
            this.filtered_page_list = filtered_list.sort((a, b) => (a.name > b.name) ? 1 : -1);
            this.reloadPagination();
        },
        reloadPagination() {
            this.page_count = Math.ceil(this.filtered_page_list.length / this.num_per_page);
            this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
            this.getItemInterval();
        },
        getItemInterval() {
            const from = this.page_index * this.num_per_page + 1;
            let to = (this.page_index + 1) * this.num_per_page;
            if (to > this.filtered_page_list.length) {
                to = this.filtered_page_list.length;
            }
            this.item_interval = from >= to ? to : `${from} - ${to}`;
            this.available_page_list = this.filtered_page_list.slice(from - 1, to);
        },
        moveToPrevPage() {
            this.page_index--;
            this.getItemInterval();
        },
        moveToPage(page) {
            this.page_index = page - 1;
            this.getItemInterval();
        },
        moveToNextPage() {
            this.page_index++;
            this.getItemInterval();
        },
        addPageSelectionToPPT(page, index) {
            const page_temp = { ...page };
            page_temp.order = this.selected_page_list.length + 1;
            page_temp.include_linked_sections = false;
            page_temp.blend_sections = false;
            this.selected_page_list.push(page_temp);

            const page_id = page.id;
            const page_index = this.filtered_page_list.findIndex(page => page.id === page_id);
            this.filtered_page_list.splice(page_index, 1);

            this.reloadPagination();
            
            // Initialize tooltips after the DOM updates
            this.$nextTick(() => {
                const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
                const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

                for (let i = 0; i < tooltipTriggerList.length; i++) {
                    (function(index) {
                        tooltipTriggerList[index].addEventListener('click', function() {
                            tooltipList[index].hide();
                        });
                        tooltipTriggerList[index].addEventListener('mouseleave', function() {
                            tooltipList[index].hide();
                        });
                    })(i);
                }
            });
        },
        removePageSelection(page, index) {
            this.filtered_page_list.push(page);
            this.selected_page_list.splice(index, 1);
            this.filtered_page_list.sort((a, b) => (a.name > b.name) ? 1 : -1);
            this.adjustOrderInSelectedPageList();
            this.reloadPagination();
        },
        adjustOrderInSelectedPageList() {
            this.selected_page_list.forEach((page, index) => {
                page.order = index + 1;
            });
        },
        toggleFilterPanel() {
            this.is_filter_panel_expanded = !this.is_filter_panel_expanded;
        },
        updateFilters(filter_list) {
            this.selected_filter_list = filter_list;
        },
        updateFilterCount(filter_count) {
            this.filter_count = filter_count;
        },
        downloadPPT() {
            this.form.busy = true;
            this.selected_page_list.forEach(page => {
                page.dimensions.forEach(dimension => {
                    const filter = this.selected_filter_list.find(filter => filter.filter_id === dimension.id);
                    if (filter) {
                        dimension.value = filter.filter_value;
                    }
                });
            });

            Swal.fire({
                title: "Your Presentation is Being Built",
                html: "It will take a few minutes to compile all of the data. In the meantime, you can enter a presentation name below which will save the template to be downloaded or duplicated in the future.",
                input: "text",
                inputLabel: "Presentation Name",
                showCancelButton: true,
                confirmButtonText: "Save",
                cancelButtonText: "No Thanks",
                customClass: {
                    popup: 'swal-success'
                },
                preConfirm: (presentationName) => {
                    if (presentationName) {
                        // Will be saved after export_id is received
                        return presentationName;
                    }
                }
            }).then((result) => {
                if (result.isConfirmed && result.value) {
                    // Store the name to save after export_id is received
                    this.pendingPresoName = result.value;
                }

                // Start the export
                window.axios.post('/api/export/ppt', { pages: this.selected_page_list })
                    .then(response => {
                        this.export_id = response.data.export_id;
                        
                        // Show initial progress notification
                        // Backend will dispatch ProgressNotification events that will update this notification
                        const notificationId = `ppt-${this.export_id}`;
                        this.showProgressNotification({
                            notificationId: notificationId,
                            icon: '/img/icons/nav-icons/Insights.svg',
                            text: 'Your presentation is currently being generated. Feel free to explore other pages while you wait.',
                            percentage: 0,
                            status: 'working'
                        });

                        // Save the presentation name if provided
                        if (this.pendingPresoName) {
                            this.savePresoName(this.pendingPresoName);
                            this.pendingPresoName = null;
                        }
                        
                    })
                    .catch(error => {
                        const notificationId = `ppt-error-${Date.now()}`;
                        this.showProgressNotification({
                            notificationId: notificationId,
                            icon: '/img/icons/avid-icons/danger.svg',
                            text: 'There was an error starting the presentation generation. Please try again.',
                            percentage: 0,
                            status: 'error'
                        });
                    })
                    .finally(() => {
                        this.form.busy = false;
                    });
            });
        },
        savePresoName(name) {
            if (this.export_id) {
                window.axios.put('/api/export/ppt/' + this.export_id, { name: name });
            }
        }
    }
});
</script>